// app/(admin)/combos/[id]/subscriptions/page.tsx

import { useState, useEffect, useCallback, useMemo } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import PageTitle from "../../../../../components/ui/PageTitle";
import LoadingIndicator from "../../../../../components/ui/LoadingIndicator";
import ErrorMessage from "../../../../../components/ui/ErrorMessage";
import SubscriptionTable from "../../../../../components/admin/SubscriptionTable";
import FilterControls from "../../../../../components/admin/FilterControls";
import PaginationControls from "../../../../../components/ui/PaginationControls";
import Button from "../../../../../components/ui/Button";
import ExtendModal from "../../../../../components/admin/ExtendModal";
import ConfirmationModal from "../../../../../components/ui/ConfirmationModal";
import EmptyState from "../../../../../components/ui/EmptyState";
import Input from "../../../../../components/ui/Input";

import {
  getAdminComboSubscriptions,
  getComboDetails,
  extendSubscription,
  revokeSubscription,
} from "../../../../../lib/apiClient";

import {
  SubscriptionAdminResponse,
  PopulatedCombo,
} from "../../../../../types";

import { getErrorMessage, formatDate } from "../../../../../lib/utils";
import { useDebounce } from "../../../../../lib/hooks";
import { SUBSCRIPTION_STATUS, ROUTES } from "../../../../../lib/constants";
import toast from "react-hot-toast";

const ITEMS_PER_PAGE = 6;

export default function ComboSubscriptionsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const params = useParams();
  const comboId = params.id as string;

  // State
  const [combo, setCombo] = useState<PopulatedCombo | null>(null);
  const [subscriptions, setSubscriptions] = useState<SubscriptionAdminResponse[]>([]);
  const [filteredSubs, setFilteredSubs] = useState<SubscriptionAdminResponse[]>([]);
  const [filly, setFilly] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [phoneFilter, setPhoneFilter] = useState(searchParams.get("phone") || "");
  const [statusFilter, setStatusFilter] = useState(searchParams.get("status") || "");
  const [currentPage, setCurrentPage] = useState(Number(searchParams.get("page") || "1"));

  // Modals
  const [isExtendModalOpen, setIsExtendModalOpen] = useState(false);
  const [isRevokeModalOpen, setIsRevokeModalOpen] = useState(false);
  const [selectedSubscription, setSelectedSubscription] = useState<SubscriptionAdminResponse | null>(null);
  const [modalLoading, setModalLoading] = useState(false);

  // Date Filters
  const today = new Date();
  const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const lastDayOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
  const formatDefaultDate = (date: Date) => date.toISOString().split("T")[0];
  const [startDate, setStartDate] = useState<string>(formatDefaultDate(firstDayOfMonth));
  const [endDate, setEndDate] = useState<string>(formatDefaultDate(lastDayOfMonth));
  const handleStartDateChange = (e: React.ChangeEvent<HTMLInputElement>) => setStartDate(e.target.value);
  const handleEndDateChange = (e: React.ChangeEvent<HTMLInputElement>) => setEndDate(e.target.value);

  // Fetch Combo Details
  useEffect(() => {
    const fetchComboInfo = async () => {
      if (!comboId) return;
      try {
        const data = await getComboDetails(comboId);
        setCombo(data);
      } catch (err) {
        console.error("Failed to fetch combo:", getErrorMessage(err));
        setError("Could not load combo details, but attempting to load subscriptions.");
      }
    };
    fetchComboInfo();
  }, [comboId]);

  const debouncedPhoneFilter = useDebounce(phoneFilter, 500);
  const debouncedStatusFilter = useDebounce(statusFilter, 500);

  // Apply filters to subscriptions data
  const filteredSubscriptions = useMemo(() => {
    let filtered = subscriptions;
    if (debouncedPhoneFilter) {
      filtered = filtered.filter(sub =>
        sub.user_id?.phone?.startsWith(debouncedPhoneFilter) ||
        sub.user_id?.phone?.includes(debouncedPhoneFilter)
      );
    }
    if (debouncedStatusFilter) {
      filtered = filtered.filter(sub => sub.status === debouncedStatusFilter);
    }

    const checkS = new Date(startDate);
    const checkE = new Date(endDate);
    if (checkS <= checkE) {
      filtered = filtered.filter(sub => {
        const createdAt = new Date(sub.createdAt);
        return createdAt >= checkS && createdAt <= checkE;
      });
    }
    return filtered;
  }, [subscriptions, debouncedPhoneFilter, debouncedStatusFilter, filly, startDate, endDate]);

  // Reset to page 1 when filters change
  useEffect(() => {
    if (currentPage !== 1) {
      setCurrentPage(1);
    }
  }, [debouncedPhoneFilter, debouncedStatusFilter, startDate, endDate, filly]);

  // Fetch Subscriptions
  const fetchSubscriptions = useCallback(async (page: number, phone: string, status: string) => {
    if (!comboId) return;
    setIsLoading(true);
    setError(null);

    // Update URL
    const params = new URLSearchParams(window.location.search);
    if (page > 1) params.set("page", String(page)); else params.delete("page");
    phone ? params.set("phone", phone) : params.delete("phone");
    status ? params.set("status", status) : params.delete("status");
    window.history.pushState({}, "", `${window.location.pathname}?${params.toString()}`);

    try {
      const filters: any = { userPhone: phone || undefined, status: status || undefined };
      const data = await getAdminComboSubscriptions(comboId, filters);
      const rawSubs = Array.isArray(data.subscriptions)
        ? data.subscriptions.flatMap((channel: any) =>
          (channel.subscriptions || []).map((sub: any) => ({
            ...sub,
            user_phone: sub.user_id?.phone || null,
            plan_name: sub.plan_id?.name || (typeof sub.plan_id === "string" ? sub.plan_id : undefined),
            channel_name: channel.channel_name || sub.channel_id?.name,
          }))
        )
        : [];

      const processed: SubscriptionAdminResponse[] = rawSubs;

      // Combine real data with dummy data for testing
      const combinedSubscriptions = processed;
      setSubscriptions(combinedSubscriptions);
      setCurrentPage(page);
    } catch (err) {
      const msg = getErrorMessage(err);
      setError(msg);
      toast.error(`Error loading subscriptions: ${msg}`);
    } finally {
      setIsLoading(false);
    }
  }, [comboId]);

  // Initial data fetch when component mounts
  useEffect(() => {
    fetchSubscriptions(1, "", "");
  }, [comboId, fetchSubscriptions]);

  // --- Actions ---
  const handleExtendClick = (sub: SubscriptionAdminResponse) => { setSelectedSubscription(sub); setIsExtendModalOpen(true); };
  const handleRevokeClick = (sub: SubscriptionAdminResponse) => { setSelectedSubscription(sub); setIsRevokeModalOpen(true); };

  const handleConfirmExtend = async (extensionDays: number) => {
    if (!selectedSubscription) return;
    setModalLoading(true);
    try {
      await extendSubscription(selectedSubscription._id, { extension_days: extensionDays });
      toast.success(`Subscription extended by ${extensionDays} days.`);
      setIsExtendModalOpen(false); setSelectedSubscription(null);
      // Refetch data to get updated subscription status
      fetchSubscriptions(1, "", "");
    } catch (err) {
      toast.error(`Failed to extend: ${getErrorMessage(err)}`);
    } finally { setModalLoading(false); }
  };

  const handleConfirmRevoke = async () => {
    if (!selectedSubscription) return;
    setModalLoading(true);
    try {
      await revokeSubscription(selectedSubscription._id);
      toast.success("Subscription revoked successfully.");
      setIsRevokeModalOpen(false); setSelectedSubscription(null);
      // Refetch data to get updated subscription status
      fetchSubscriptions(1, "", "");
    } catch (err) {
      toast.error(`Failed to revoke: ${getErrorMessage(err)}`);
    } finally { setModalLoading(false); }
  };

  const handlePageChange = (newPage: number) => setCurrentPage(newPage);
  const handleFilterChange = (filters: { phone?: string; status?: string }) => {
    setPhoneFilter(filters.phone ?? "");
    setStatusFilter(filters.status ?? "");
  };
  const handleClearFilters = () => { setPhoneFilter(""); setStatusFilter(""); setCurrentPage(1); };

  const statusOptions = useMemo(() => [
    { value: "", label: "All Statuses" },
    { value: SUBSCRIPTION_STATUS.ACTIVE, label: "Active" },
    { value: SUBSCRIPTION_STATUS.EXPIRED, label: "Expired" },
    { value: SUBSCRIPTION_STATUS.REVOKED, label: "Revoked" },
    { value: SUBSCRIPTION_STATUS.PENDING, label: "Pending" },
  ], []);

  const handleExportCSV = () => {
    if (!filteredSubscriptions.length) return;

    const headers = ["Name", "Phone", "Plan",
      "Email",
      "Status", "Sub On", "End Date", "Aadhar Card", "PAN Card",
      "DoB", "Combo Name", "Transaction Ammount"
    ];
    const rows = filteredSubscriptions.map(sub => [
      sub.user_id?.name || "N/A",
      sub.user_id?.phone || "N/A",
      sub.user_id?.email || "N/A",
      sub.plan_name || "N/A",
      sub.status,
      formatDate(sub.createdAt),
      formatDate(sub.end_date),
      String(sub.user_id?.aadhar_number) || "Pending",
      sub.user_id?.pan_number || "Pending",
      sub.user_id?.date_of_birth || "Pending",
      combo?.name || "N/A",
      sub.totalTransactionAmount || "N/A"

    ]);
    const csvContent = [headers.join(","), ...rows.map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(","))].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.setAttribute("download", "combo_sub_export.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const RenderSubscriptionContent = () => {
    const paginatedSubs = useMemo(() => {
      const startIdx = (currentPage - 1) * ITEMS_PER_PAGE;
      return filteredSubscriptions.slice(startIdx, startIdx + ITEMS_PER_PAGE);
    }, [filteredSubscriptions, currentPage]);

    if (isLoading && subscriptions.length === 0) return <div className="flex justify-center items-center min-h-[200px]"><LoadingIndicator text="Loading subscriptions..." /></div>;
    if (error) return <div className="text-center p-4">
      <ErrorMessage title="Error Loading Subscriptions" message={error} />
      <Button onClick={() => fetchSubscriptions(currentPage, debouncedPhoneFilter, statusFilter)} className="mt-4">Retry Load</Button>
    </div>;
    if (!isLoading && filteredSubscriptions.length === 0) return <EmptyState message={phoneFilter || statusFilter ? "No subscriptions match the current filters." : "No subscriptions found for this combo yet."}>
      {(phoneFilter || statusFilter) && <Button onClick={handleClearFilters} variant="secondary" size="sm" className="mt-2">Clear Filters</Button>}
    </EmptyState>;

    return <>
      <SubscriptionTable iscombo={true} subscriptions={paginatedSubs} isLoading={isLoading} onExtend={handleExtendClick} onRevoke={handleRevokeClick} />
      <PaginationControls currentPage={currentPage} totalItems={filteredSubscriptions.length} itemsPerPage={ITEMS_PER_PAGE} onPageChange={handlePageChange} className="px-4 py-3" />
    </>;
  };

  return (
    <div>
      <div className="mb-6">
        <Button onClick={() => navigate(ROUTES.ADMIN_COMBOS)} variant="link" size="sm" className="mb-1 text-text-secondary hover:text-text-primary">&larr; Back to My Combos</Button>
        <PageTitle title={`Manage Subscriptions`} subtitle={`For Combo: ${combo?.name || comboId}`} />
      </div>

      <FilterControls phoneFilter={phoneFilter} statusFilter={statusFilter} onFilterChange={handleFilterChange} statusOptions={statusOptions} handleExportCSV={handleExportCSV} />

      <div className="mb-6 rounded-lg shadow bg-dark-secondary border border-dark-tertiary">
        <div className="px-4 pt-4"><h2 className="text-lg font-semibold text-white mb-4">Subscription Date Filter</h2></div>
        <div className="flex flex-wrap items-center gap-4 px-4 pb-6">
          <Input label="Start Date" id="start-date" name="start-date" type="date" value={startDate} onChange={handleStartDateChange} containerClassName="mb-0" disabled={isLoading} />
          <Input label="End Date" id="end-date" name="end-date" type="date" value={endDate} onChange={handleEndDateChange} containerClassName="mb-0" disabled={isLoading} />
          <Button onClick={() => setFilly(!filly)} disabled={isLoading}>Apply Filter</Button>
        </div>
      </div>

      <div className="bg-dark-secondary rounded-lg shadow-md overflow-hidden">{RenderSubscriptionContent()}</div>

      <ExtendModal isOpen={isExtendModalOpen} onClose={() => setIsExtendModalOpen(false)} onConfirm={handleConfirmExtend} isLoading={modalLoading} subscription={selectedSubscription} />
      <ConfirmationModal isOpen={isRevokeModalOpen} onClose={() => setIsRevokeModalOpen(false)} onConfirm={handleConfirmRevoke} isLoading={modalLoading} title="Confirm Revocation" message={<>
        Are you sure you want to revoke the subscription for user <strong className="text-text-primary">{selectedSubscription?.user_phone}</strong>?
        <br />
        <span className="text-sm text-functional-warning">Their access will be removed immediately.</span>
      </>} confirmText="Yes, Revoke Subscription" confirmButtonVariant="danger" />
    </div>
  );
}
